/** @file Weights panel: the mesh line, rigid/skinned, convert, rebind at setup and smooth buttons. */
import type { CommandCall, Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { Button } from '../../ui-kit/Button';
import { weightsCalls } from './weightsCalls';
import type { WeightsView } from './weightsView';

/** Props every Weights panel section shares. */
export interface SectionProps {
  view: WeightsView;
  doc: Doc;
  t: Translate;
  commit: (calls: readonly CommandCall[]) => void;
}

/**
 * Binding of the edited mesh: rigid ↔ skinned on the slot bone; rebind and smooth for skinned meshes. In Animate mode a
 * stale bind is not re-captured on its own, so the section says so once beside Rebind at setup (T-473).
 */
export function BindingSection({ view, doc, t, commit, animate }: SectionProps & { animate: boolean }) {
  const skinned = view.binding === 'skinned';
  const convert = (): void => { commit([weightsCalls.binding(doc, view.target, skinned ? 'rigid' : 'skinned')]); };
  const smooth = (): void => { commit(weightsCalls.edit(doc, view.target, weightsCalls.smooth(view.target, view.selected), animate)); };
  const staleNote = animate && skinned && view.stale;
  return (
    <section className="weights-section" aria-label={t('panels.weights.binding')}>
      <p className="weights-line">{t('panels.weights.meshLine', { mesh: view.meshName, slot: view.slotName, n: view.vertexCount })}</p>
      <p className="weights-line">{t(skinned ? 'panels.weights.skinned' : 'panels.weights.rigid')}</p>
      <div className="weights-buttons">
        <Button label={t(skinned ? 'panels.weights.makeRigid' : 'panels.weights.makeSkinned')} onClick={convert} />
        <Button label={t('panels.weights.rebind')} primary={staleNote} disabled={!skinned} onClick={() => { commit([weightsCalls.rebind(view.target)]); }} />
        <Button label={t('panels.weights.smooth')} disabled={!skinned} onClick={smooth} />
      </div>
      {staleNote ? <p className="weights-muted" role="status">{t('tools.paintWeights.staleBind')}</p> : null}
    </section>
  );
}
